"use client";

import { c as _c } from "react/compiler-runtime";
import { hashKey, keepPreviousData, useQueries, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { AnalyticsFilters, AnalyticsGroupedResponse, AnalyticsRow, AnalyticsSummary, GroupPageResponse, PaginationParams } from "./types";

// ============================================================================
// useColumnVisibility
// ============================================================================

/**
 * Hook to manage column visibility for analytics tables
 */
export function useColumnVisibility(initialColumns) {
  const $ = _c(8);
  let t0;
  if ($[0] !== initialColumns) {
    t0 = () => new Set(initialColumns);
    $[0] = initialColumns;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [visibleColumns, setVisibleColumns] = useState(t0);
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = key => {
      setVisibleColumns(prev => {
        const next = new Set(prev);
        if (next.has(key)) {
          next.delete(key);
        } else {
          next.add(key);
        }
        return next;
      });
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const toggleColumn = t1;
  let t2;
  if ($[3] !== visibleColumns) {
    t2 = key_0 => visibleColumns.has(key_0);
    $[3] = visibleColumns;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const isColumnVisible = t2;
  let t3;
  if ($[5] !== isColumnVisible || $[6] !== visibleColumns) {
    t3 = {
      visibleColumns,
      toggleColumn,
      setVisibleColumns,
      isColumnVisible
    };
    $[5] = isColumnVisible;
    $[6] = visibleColumns;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}

// ============================================================================
// useAnalyticsSort
// ============================================================================

/**
 * Hook to manage sort state for analytics tables
 */
export function useAnalyticsSort(defaultKey, t0) {
  const $ = _c(6);
  const defaultDirection = t0 === undefined ? "desc" : t0;
  let t1;
  if ($[0] !== defaultDirection || $[1] !== defaultKey) {
    t1 = defaultKey ? {
      key: defaultKey,
      direction: defaultDirection
    } : null;
    $[0] = defaultDirection;
    $[1] = defaultKey;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const [sortConfig, setSortConfig] = useState(t1);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = key => {
      setSortConfig(prev => {
        if (prev?.key === key) {
          return {
            key,
            direction: prev.direction === "asc" ? "desc" : "asc"
          };
        }
        return {
          key,
          direction: "desc"
        };
      });
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleSort = t2;
  let t3;
  if ($[4] !== sortConfig) {
    t3 = {
      sortConfig,
      handleSort,
      setSortConfig
    };
    $[4] = sortConfig;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}

// ============================================================================
// useExpandedGroups
// ============================================================================

interface UseExpandedGroupsOptions {
  /** Group keys to initialize expansion state */
  groupKeys?: string[];
  /** Whether groups should be expanded by default */
  defaultExpanded?: boolean;
  /** Callback when expansion state changes */
  onExpansionChange?: (state: {
    expandedCount: number;
    totalGroups: number;
    isAllExpanded: boolean;
    isAllCollapsed: boolean;
  }) => void;
}

/**
 * Hook to manage expanded/collapsed state for groups
 */
export function useExpandedGroups(t0) {
  const $ = _c(24);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    groupKeys: t2,
    defaultExpanded: t3,
    onExpansionChange
  } = t1;
  let t4;
  if ($[2] !== t2) {
    t4 = t2 === undefined ? [] : t2;
    $[2] = t2;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const groupKeys = t4;
  const defaultExpanded = t3 === undefined ? false : t3;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = new Set();
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const [expandedGroups, setExpandedGroups] = useState(t5);
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [];
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const prevGroupKeysRef = useRef(t6);
  let t7;
  let t8;
  if ($[6] !== defaultExpanded || $[7] !== groupKeys) {
    t7 = () => {
      const prevKeys = prevGroupKeysRef.current;
      const keysChanged = prevKeys.length !== groupKeys.length || !prevKeys.every((key, i) => key === groupKeys[i]);
      if (keysChanged) {
        prevGroupKeysRef.current = groupKeys;
        if (defaultExpanded && groupKeys.length > 0) {
          setExpandedGroups(new Set(groupKeys));
        } else {
          setExpandedGroups(new Set());
        }
      }
    };
    t8 = [defaultExpanded, groupKeys];
    $[6] = defaultExpanded;
    $[7] = groupKeys;
    $[8] = t7;
    $[9] = t8;
  } else {
    t7 = $[8];
    t8 = $[9];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[10] !== expandedGroups.size || $[11] !== groupKeys.length || $[12] !== onExpansionChange) {
    t9 = () => {
      if (onExpansionChange) {
        const totalGroups = groupKeys.length;
        onExpansionChange({
          expandedCount: expandedGroups.size,
          totalGroups,
          isAllExpanded: totalGroups > 0 && expandedGroups.size === totalGroups,
          isAllCollapsed: expandedGroups.size === 0
        });
      }
    };
    t10 = [expandedGroups.size, groupKeys.length, onExpansionChange];
    $[10] = expandedGroups.size;
    $[11] = groupKeys.length;
    $[12] = onExpansionChange;
    $[13] = t10;
    $[14] = t9;
  } else {
    t10 = $[13];
    t9 = $[14];
  }
  useEffect(t9, t10);
  let t11;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = groupKey => {
      setExpandedGroups(prev => {
        const next = new Set(prev);
        if (next.has(groupKey)) {
          next.delete(groupKey);
        } else {
          next.add(groupKey);
        }
        return next;
      });
    };
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const toggleGroup = t11;
  let t12;
  if ($[16] !== groupKeys) {
    t12 = () => {
      setExpandedGroups(new Set(groupKeys));
    };
    $[16] = groupKeys;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  const expandAll = t12;
  let t13;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => {
      setExpandedGroups(new Set());
    };
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  const collapseAll = t13;
  const isAllExpanded = groupKeys.length > 0 && expandedGroups.size === groupKeys.length;
  const isAllCollapsed = expandedGroups.size === 0;
  let t14;
  if ($[19] !== expandAll || $[20] !== expandedGroups || $[21] !== isAllCollapsed || $[22] !== isAllExpanded) {
    t14 = {
      expandedGroups,
      toggleGroup,
      expandAll,
      collapseAll,
      isAllExpanded,
      isAllCollapsed
    };
    $[19] = expandAll;
    $[20] = expandedGroups;
    $[21] = isAllCollapsed;
    $[22] = isAllExpanded;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  return t14;
}

// ============================================================================
// useGroupPagination
// ============================================================================

interface UseGroupPaginationOptions<TRow extends AnalyticsRow, TSummary extends AnalyticsSummary, TFilters extends AnalyticsFilters, TResponse extends AnalyticsGroupedResponse<TRow, TSummary>> {
  /** Base query key for cache management */
  baseQueryKey: readonly unknown[];
  /** Query function that handles both initial load and page fetches */
  queryFn: (filters: TFilters & {
    pagination: PaginationParams;
  }) => Promise<TResponse | GroupPageResponse<TRow>>;
  /** Current filters */
  filters: TFilters;
  /** Number of rows per page */
  pageSize: number;
  /** Whether queries are enabled */
  enabled?: boolean;
  /**
   * The answer's shape (e.g. grouped or flat). The previous answer stands in
   * while a new one loads only when its shape matches, so a flat table never
   * draws the rows of the grouped answer it is replacing.
   */
  placeholderScope?: unknown;
}
interface UseGroupPaginationReturn<TResponse> {
  /** Merged data with current page rows per group */
  data: TResponse | undefined;
  /** Whether initial load is in progress */
  isLoading: boolean;
  /** Error from initial load */
  error: Error | null;
  /** Get current page for a group (1-indexed) */
  getGroupPage: (groupKey: string) => number;
  /** Set page for a group */
  setGroupPage: (groupKey: string, page: number) => void;
  /** Check if a specific group is loading its page */
  isGroupLoading: (groupKey: string) => boolean;
}

/** The key of a paged table's first load, for a caller that reads the same response. */
export function groupPaginationQueryKey(baseQueryKey: readonly unknown[], pageSize: number) {
  return [...baseQueryKey, {
    pagination: {
      pageSize
    }
  }] as const;
}

/**
 * Hook to manage per-group pagination for analytics tables.
 *
 * Uses React Query to:
 * 1. Fetch initial data (all groups + first page each)
 * 2. Fetch additional pages on demand using useQueries
 * 3. Merge page data with initial response
 */
export function useGroupPagination(t0) {
  const $ = _c(69);
  const {
    baseQueryKey,
    queryFn,
    filters,
    pageSize,
    enabled: t1,
    placeholderScope
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  let t2;
  if ($[0] !== baseQueryKey) {
    t2 = hashKey(baseQueryKey);
    $[0] = baseQueryKey;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const scope = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {};
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== scope) {
    t4 = {
      scope,
      pages: t3
    };
    $[3] = scope;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const [paging, setPaging] = useState(t4);
  let t5;
  if ($[5] !== paging.pages || $[6] !== paging.scope || $[7] !== scope) {
    t5 = paging.scope === scope ? paging.pages : {};
    $[5] = paging.pages;
    $[6] = paging.scope;
    $[7] = scope;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const groupPages = t5;
  let t6;
  if ($[9] !== baseQueryKey || $[10] !== pageSize) {
    t6 = groupPaginationQueryKey(baseQueryKey, pageSize);
    $[9] = baseQueryKey;
    $[10] = pageSize;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== filters || $[13] !== pageSize || $[14] !== queryFn) {
    t7 = () => queryFn({
      ...filters,
      pagination: {
        pageSize
      }
    } as TFilters & {
      pagination: PaginationParams;
    });
    $[12] = filters;
    $[13] = pageSize;
    $[14] = queryFn;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  let t9;
  if ($[16] !== placeholderScope) {
    t8 = {
      placeholderScope
    };
    t9 = (previous, previousQuery) => previousQuery?.meta?.placeholderScope === placeholderScope ? keepPreviousData(previous) : undefined;
    $[16] = placeholderScope;
    $[17] = t8;
    $[18] = t9;
  } else {
    t8 = $[17];
    t9 = $[18];
  }
  let t10;
  if ($[19] !== enabled || $[20] !== t6 || $[21] !== t7 || $[22] !== t8 || $[23] !== t9) {
    t10 = {
      queryKey: t6,
      queryFn: t7,
      enabled,
      meta: t8,
      placeholderData: t9
    };
    $[19] = enabled;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const initialQuery = useQuery(t10);
  const firstLoadVersion = initialQuery.dataUpdatedAt;
  let t11;
  if ($[25] !== groupPages) {
    t11 = Object.entries(groupPages).filter(_temp).map(_temp2);
    $[25] = groupPages;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  const groupsNeedingFetch = t11;
  let t12;
  if ($[27] !== baseQueryKey || $[28] !== enabled || $[29] !== filters || $[30] !== firstLoadVersion || $[31] !== groupsNeedingFetch || $[32] !== initialQuery.data || $[33] !== initialQuery.isPlaceholderData || $[34] !== pageSize || $[35] !== queryFn) {
    t12 = {
      queries: groupsNeedingFetch.map(t13 => {
        const {
          key: key_0,
          page: page_1
        } = t13;
        return {
          queryKey: [...baseQueryKey, "page", key_0, page_1, firstLoadVersion],
          queryFn: () => queryFn({
            ...filters,
            pagination: {
              pageSize,
              groupKey: key_0,
              page: page_1
            }
          } as TFilters & {
            pagination: PaginationParams;
          }),
          staleTime: 300000,
          placeholderData: keepPreviousData,
          enabled: enabled && !!initialQuery.data && !initialQuery.isPlaceholderData
        };
      })
    };
    $[27] = baseQueryKey;
    $[28] = enabled;
    $[29] = filters;
    $[30] = firstLoadVersion;
    $[31] = groupsNeedingFetch;
    $[32] = initialQuery.data;
    $[33] = initialQuery.isPlaceholderData;
    $[34] = pageSize;
    $[35] = queryFn;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  const pageQueries = useQueries(t12);
  let t13;
  if ($[37] !== groupsNeedingFetch || $[38] !== pageQueries) {
    const failedGroups = groupsNeedingFetch.filter((_, index) => pageQueries[index]?.isError).map(_temp3);
    t13 = JSON.stringify(failedGroups);
    $[37] = groupsNeedingFetch;
    $[38] = pageQueries;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  const failedGroupsKey = t13;
  let t14;
  let t15;
  if ($[40] !== failedGroupsKey) {
    t14 = () => {
      const failed = new Set(JSON.parse(failedGroupsKey));
      if (failed.size === 0) {
        return;
      }
      setPaging(prev => ({
        scope: prev.scope,
        pages: Object.fromEntries(Object.entries(prev.pages).filter(t16 => {
          const [key_2] = t16;
          return !failed.has(key_2);
        }))
      }));
    };
    t15 = [failedGroupsKey];
    $[40] = failedGroupsKey;
    $[41] = t14;
    $[42] = t15;
  } else {
    t14 = $[41];
    t15 = $[42];
  }
  useEffect(t14, t15);
  let t16;
  bb0: {
    if (!initialQuery.data || !("groups" in initialQuery.data)) {
      t16 = undefined;
      break bb0;
    }
    const baseData = initialQuery.data as TResponse;
    let t17;
    if ($[43] !== baseData.groups || $[44] !== groupPages || $[45] !== groupsNeedingFetch || $[46] !== pageQueries) {
      let t18;
      if ($[48] !== groupPages || $[49] !== groupsNeedingFetch || $[50] !== pageQueries) {
        t18 = group => {
          const pageNum = groupPages[group.key] || 1;
          if (pageNum === 1) {
            return group;
          }
          const queryIndex = groupsNeedingFetch.findIndex(g => g.key === group.key);
          const pageQuery = queryIndex >= 0 ? pageQueries[queryIndex] : undefined;
          if (pageQuery?.data && "rows" in pageQuery.data) {
            return {
              ...group,
              rows: pageQuery.data.rows
            };
          }
          return group;
        };
        $[48] = groupPages;
        $[49] = groupsNeedingFetch;
        $[50] = pageQueries;
        $[51] = t18;
      } else {
        t18 = $[51];
      }
      t17 = baseData.groups.map(t18);
      $[43] = baseData.groups;
      $[44] = groupPages;
      $[45] = groupsNeedingFetch;
      $[46] = pageQueries;
      $[47] = t17;
    } else {
      t17 = $[47];
    }
    let t18;
    if ($[52] !== baseData || $[53] !== t17) {
      t18 = {
        ...baseData,
        groups: t17
      };
      $[52] = baseData;
      $[53] = t17;
      $[54] = t18;
    } else {
      t18 = $[54];
    }
    t16 = t18;
  }
  const data = t16;
  let t17;
  if ($[55] !== groupPages) {
    t17 = key_3 => groupPages[key_3] || 1;
    $[55] = groupPages;
    $[56] = t17;
  } else {
    t17 = $[56];
  }
  const getGroupPage = t17;
  let t18;
  if ($[57] !== scope) {
    t18 = (key_4, page_2) => {
      setPaging(prev_0 => ({
        scope,
        pages: {
          ...(prev_0.scope === scope ? prev_0.pages : {}),
          [key_4]: page_2
        }
      }));
    };
    $[57] = scope;
    $[58] = t18;
  } else {
    t18 = $[58];
  }
  const setGroupPage = t18;
  let t19;
  if ($[59] !== groupsNeedingFetch || $[60] !== pageQueries) {
    t19 = key_5 => {
      const idx = groupsNeedingFetch.findIndex(g_0 => g_0.key === key_5);
      return idx >= 0 && (pageQueries[idx]?.isLoading ?? false);
    };
    $[59] = groupsNeedingFetch;
    $[60] = pageQueries;
    $[61] = t19;
  } else {
    t19 = $[61];
  }
  const isGroupLoading = t19;
  let t20;
  if ($[62] !== data || $[63] !== getGroupPage || $[64] !== initialQuery.error || $[65] !== initialQuery.isLoading || $[66] !== isGroupLoading || $[67] !== setGroupPage) {
    t20 = {
      data,
      isLoading: initialQuery.isLoading,
      error: initialQuery.error,
      getGroupPage,
      setGroupPage,
      isGroupLoading
    };
    $[62] = data;
    $[63] = getGroupPage;
    $[64] = initialQuery.error;
    $[65] = initialQuery.isLoading;
    $[66] = isGroupLoading;
    $[67] = setGroupPage;
    $[68] = t20;
  } else {
    t20 = $[68];
  }
  return t20;
}
function _temp3(t0) {
  const {
    key: key_1
  } = t0;
  return key_1;
}
function _temp2(t0) {
  const [key, page_0] = t0;
  return {
    key,
    page: page_0
  };
}
function _temp(t0) {
  const [, page] = t0;
  return page > 1;
}